import React, { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { API, formatApiError, setAuthToken } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import Logo from "../components/Logo";

// Llamada robusta a /auth/google-session usando fetch() nativo (sin axios).
// Por qué: tras el redirect desde auth.emergentagent.com, iOS Safari aplica ITP
// de forma agresiva y a veces aborta XHR/axios con "Network Error". Usar fetch
// con credentials:"include" + reintentos propios + timeout manual da más control
// y es más tolerante con la transición post-OAuth.
async function exchangeGoogleSession(sessionId) {
  const url = `${API}/auth/google-session`;
  const body = JSON.stringify({ session_id: sessionId });
  const maxAttempts = 3;
  let lastErr;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const ctrl = new AbortController();
    const timeoutId = setTimeout(() => ctrl.abort(), 25000);
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body,
        credentials: "include",
        signal: ctrl.signal,
        cache: "no-store",
        mode: "cors",
      });
      clearTimeout(timeoutId);
      if (!res.ok) {
        // Error HTTP real (401, 503, etc) — no reintentamos
        let detail = `HTTP ${res.status}`;
        try {
          const j = await res.json();
          if (j?.detail) detail = j.detail;
        } catch { /* ignore */ }
        const err = new Error(detail);
        err.response = { status: res.status, data: { detail } };
        throw err;
      }
      return await res.json();
    } catch (err) {
      clearTimeout(timeoutId);
      lastErr = err;
      // Si es error HTTP real, salir ya
      if (err?.response) throw err;
      // Si fue abort/network, reintentar con backoff
      if (attempt < maxAttempts) {
        await new Promise((r) => setTimeout(r, 700 * attempt));
      }
    }
  }
  throw lastErr || new Error("Network Error");
}

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = location.hash || window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate("/login", { replace: true });
      return;
    }
    const sessionId = match[1];

    (async () => {
      // Dejar que iOS Safari asiente el estado post-OAuth antes de disparar
      // la POST. Sin este delay, Safari a veces aborta la request con
      // "Network Error" en la transición.
      await new Promise((r) => setTimeout(r, 250));
      try {
        const data = await exchangeGoogleSession(sessionId);
        // Guardar token para iOS Safari / PWA (fallback si ITP descarta la cookie)
        if (data?.token) setAuthToken(data.token);
        // Clear hash FIRST so AppRouter's reactive check unmounts this component
        window.history.replaceState({}, document.title, "/dashboard");
        setUser(data);
        toast.success("Sesión iniciada con Google");
        // Small tick to let React flush setUser + hash change before navigation
        setTimeout(() => {
          navigate("/dashboard", { replace: true, state: { user: data } });
        }, 50);
      } catch (err) {
        toast.error(formatApiError(err));
        window.history.replaceState({}, document.title, "/login");
        navigate("/login", { replace: true });
      }
    })();
  }, [location, navigate, setUser]);

  return (
    <div className="min-h-screen bg-app flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <Logo size={120} className="animate-pulse" />
        <div className="text-muted uppercase tracking-widest text-sm">Autenticando...</div>
      </div>
    </div>
  );
}
